import { useFormikContext } from 'formik';
import { isNil, getOr, keyBy } from 'lodash/fp';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { type FinanceProductFormValues } from '..';
import CollapsibleWrapper, { Panel } from '../../../../../components/wrappers/CollapsibleWrapper';
import { getWorkbookToExport, exportFinancialTabsExcel } from '../../../../../utilities/excel';
import ExportExcel from '../../shared/ExportExcel';
import ImportExcel from '../../shared/ImportExcel';
import useTerms from './LeaseReferenceTable/useTerms';
import TermValueTable from './TermValueTable';

const mappedHeaders = {
    'Term*': 'term',
    'Deposit*': 'value',
};

export type DepositTableProps = {
    disabled?: boolean;
};

const DepositTable = ({ disabled = false }: DepositTableProps) => {
    const { t } = useTranslation(['financeProductDetails']);
    const { values, setFieldValue } = useFormikContext<FinanceProductFormValues>();

    const {
        displayName,
        deposit: { table: depositTable },
        term: termSettings,
    } = values;

    const terms = useTerms(termSettings);

    const onImport = useCallback(
        async data => {
            const tableDataList = data
                .filter(record => !isNil(record.value))
                .map(value => ({
                    term: value.term,
                    value: value.value,
                }));

            setFieldValue('deposit.table', tableDataList);
        },
        [setFieldValue]
    );

    const exportExcel = useCallback(async () => {
        const workbook = await getWorkbookToExport(mappedHeaders);
        const worksheet = workbook.getWorksheet(1);
        const depositTableByTerm = keyBy('term', depositTable || []);

        terms.forEach(term => {
            worksheet.addRow({ term, value: getOr(null, `${term}.value`, depositTableByTerm) });
        });

        exportFinancialTabsExcel(workbook, displayName, 'Deposit');
    }, [displayName, terms, depositTable]);

    return (
        <CollapsibleWrapper defaultActiveKey={['deposit']}>
            <Panel key="deposit" header={t('financeProductDetails:tables.deposit.title')}>
                <TermValueTable
                    disabled={disabled}
                    settingsKey="deposit"
                    valueTitle={t('financeProductDetails:tables.deposit.title')}
                />
            </Panel>
            <ImportExcel
                mappedHeaders={mappedHeaders}
                onImport={onImport}
                title={t('financeProductDetails:importModal.titles.deposit')}
            />
            <ExportExcel onClick={exportExcel} />
        </CollapsibleWrapper>
    );
};

export default DepositTable;
